import React, { useState } from 'react';
import { connect } from 'react-redux';
import LocationInput from './LocationInput';
import styled from 'styled-components';
import { LabelLarge, TextButton } from '../../elements';
import { setExistingMailingAddress, addPotentialAddress } from '../../actions/application';
import { selectPolicyTypes, selectMailingAddressInfo, selectFirstLocation } from '../../selectors/application';
import { selectPotentialMailingAddresses } from '../../selectors/index';
import { PolicyType } from '../../types/enums';
import Checkbox from '../../elements/Checkbox';

interface PropTypes {
  applicationTypes: PolicyType[];
  locations: ApplicationLocation[];
  mailingAddressInfo: Address;
  addInsAddress?: Address;
  setAddInsAddress?: Function;
  setExistingMailingAddress: (address: Address) => void;
  addAddress: (address: Address) => void;
  firstLocation?: ApplicationLocation;
}

const AddressInput = ({
  applicationTypes,
  locations,
  mailingAddressInfo,
  addInsAddress,
  setAddInsAddress,
  setExistingMailingAddress,
  addAddress,
  firstLocation,
}: PropTypes) => {
  const [showInputBox, setShowInputBox] = useState(
    setAddInsAddress ? !locations[0]?.street : !applicationTypes.includes(PolicyType.BOP) && !locations[0]?.street
  );

  if (!setAddInsAddress && locations.length < 1) {
    const seedAddress = mailingAddressInfo?.street
      ? mailingAddressInfo
      : applicationTypes.includes(PolicyType.BOP)
      ? firstLocation
      : undefined;
    if (seedAddress?.street && seedAddress?.city && seedAddress?.state && seedAddress?.zip) {
      addAddress({
        street: seedAddress.street,
        city: seedAddress.city,
        state: seedAddress.state,
        zip: seedAddress.zip,
      });
    }
  }

  const toggleInputBox = () => {
    setShowInputBox(!showInputBox);
  };

  const addNewAddress = (address: Address) => {
    const { street, city, state, zip } = address;
    setShowInputBox((p) => {
      p = false;
      addAddress({ city, state, zip, street });
      return p;
    });
  };

  const setExistingAddress = (address: Address) => {
    if (address.street === mailingAddressInfo.street) {
      setExistingMailingAddress({
        city: '',
        state: '',
        zip: '',
        street: '',
      });
    } else {
      setExistingMailingAddress({
        city: address.city,
        state: address.state,
        zip: address.zip,
        street: address.street,
      });
    }
  };

  if (!setAddInsAddress && !locations.length && applicationTypes.includes(PolicyType.BOP)) return null;

  return (
    <React.Fragment>
      <LabelLarge>Mailing Address</LabelLarge>
      {showInputBox && locations.length > 0 && (
        <ExistingAddressButton data-cy="select-existing-address" type="button" onClick={toggleInputBox}>
          Select existing address
        </ExistingAddressButton>
      )}
      {showInputBox ? (
        <LocationInput addLocation={addNewAddress} showInputBox={showInputBox} isMailingAddress />
      ) : (
        <React.Fragment>
          {locations.map(({ street, city, state, zip }) => {
            if (!street || !city || !state || !zip) return null;
            const selected = mailingAddressInfo.street === street;
            return (
              <QuestionCont key={street}>
                <Checkbox
                  onChange={() => {
                    setExistingAddress({
                      city: city,
                      state: state,
                      zip: zip,
                      street: street,
                    });
                  }}
                  selected={selected}
                  label={`${street} ${city}, ${state} ${zip}`}
                  id="existing-address-checkbox"
                />
              </QuestionCont>
            );
          })}
        </React.Fragment>
      )}
      {!showInputBox && (
        <TextButton data-cy="enter-different-address" type="button" onClick={toggleInputBox}>
          Enter a different address
        </TextButton>
      )}
    </React.Fragment>
  );
};
interface OwnPropTypes {
  addInsAddress?: Address;
  setAddInsAddress?: Function;
}

const mapStateToProps = (state: ReduxState, ownProps: OwnPropTypes) => {
  const aiAddress = ownProps.addInsAddress;
  const potentialLocations = selectPotentialMailingAddresses(state);
  // In AI context, also expose the locally saved address as a selectable
  // checkbox option so the user gets visual confirmation after Save.
  const shouldShowAIAddress =
    !!ownProps.setAddInsAddress &&
    !!aiAddress?.street &&
    !potentialLocations.some((l: Address) => l.street === aiAddress.street);
  const locations = shouldShowAIAddress
    ? [...potentialLocations, aiAddress as ApplicationLocation]
    : potentialLocations;
  return {
    locations,
    mailingAddressInfo: aiAddress || selectMailingAddressInfo(state),
    applicationTypes: selectPolicyTypes(state),
    firstLocation: selectFirstLocation(state),
  };
};

const mapDispatchToProps = (dispatch: Function, ownProps: OwnPropTypes) => {
  return {
    addAddress: (address: Address) =>
      ownProps.setAddInsAddress ? ownProps.setAddInsAddress(address) : dispatch(addPotentialAddress(address)),
    setExistingMailingAddress: (address: Address) =>
      ownProps.setAddInsAddress ? ownProps.setAddInsAddress(address) : dispatch(setExistingMailingAddress(address)),
  };
};

const QuestionCont = styled.div`
  width: 100%;
  display: flex;
  align-content: flex-start;
  justify-content: space-between;
  padding: 10px 0 0 0;
  margin-bottom: 0px;
  :first-of-type {
    padding: 10px 0;
  }
  :last-of-type {
    margin-bottom: 16px;
  }
`;

const ExistingAddressButton = styled(TextButton)`
  margin-top: 8px;
  margin-bottom: 8px;
`;

export default connect(mapStateToProps, mapDispatchToProps)(AddressInput);
